// @flow strict

import React from 'react';
import { Box } from '@material-ui/core';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import { Author } from '~components/author/author';
import { createLinkPath } from '~utils/helpers/create-link-path';

import type { PostNode } from '~types/post';

import * as styles from './post-preview.module.less';

/**
 * Type of component properties
 */
type PostPreviewProps = {
  post: PostNode,
  hideImage?: boolean,
  displayCategory?: boolean,
  className?: string,
};

/**
 * Shows preview of blog post.
 *
 * @param {PostPreviewProps} props - component properties
 *
 * @returns {Node}
 */
export const PostPreview = (props: PostPreviewProps) => {
  const { post, hideImage, displayCategory, className } = props;

  // render nothing if post is missing
  if (post == null) {
    return null;
  }

  const { title, thumbnail, permalink, language, category, date } =
    post.frontmatter;

  const timeToRead = post.timeToRead;

  return (
    <article className={`${styles.post} ${className || ''}`}>
      <Link className={styles.postWrapper} to={permalink}>
        <Box component="header" display={{ sm: 'none' }}>
          <h2 className={styles.headline}>{title}</h2>
        </Box>

        {thumbnail != null && (
          <figure>
            <GatsbyImage
              className={`${styles.image} ${
                hideImage === true ? styles.hideImage : ''
              }`}
              image={thumbnail.childImageSharp.gatsbyImageData}
              alt="Blog post thumbnail"
            />
          </figure>
        )}

        <Box component="header" display={{ xs: 'none', sm: 'block' }}>
          {displayCategory === true && (
            <p className={styles.category}>{category}</p>
          )}
          <h2 className={styles.headline}>{title}</h2>
        </Box>

        <p className={styles.excerpt}>{post.excerpt}</p>
      </Link>

      <Author
        authors={post.fields.authors}
        date={date}
        timeToRead={timeToRead}
      />
    </article>
  );
};
